/**
 * Branded Types for Type-Safe IDs
 *
 * Prevents accidental ID mixing at compile time:
 * - FloorId cannot be assigned to ProjectId
 * - Compile-time errors instead of runtime bugs
 * - Zero runtime overhead (nominal typing)
 */

declare const __brand: unique symbol

/**
 * Brand a primitive type with a nominal type tag
 */
export type Branded<T, Brand extends string> = T & {
  readonly [__brand]: Brand
}

/**
 * Type-safe Floor ID
 */
export type FloorId = Branded<string, 'FloorId'>

/**
 * Type-safe Project/Lot ID
 */
export type ProjectId = Branded<string, 'ProjectId'>

/**
 * Type-safe Element ID (JointJS element)
 */
export type ElementId = Branded<string, 'ElementId'>

/**
 * Type-safe Type ID (parking type)
 */
export type TypeId = Branded<string, 'TypeId'>

/**
 * Type-safe Relationship Key
 */
export type RelationshipKey = Branded<string, 'RelationshipKey'>

/**
 * Branded type constructors (runtime identity functions)
 * These are safe type casts that enforce type safety at compile time
 */
export const FloorId = (id: string): FloorId => id as FloorId
export const ProjectId = (id: string): ProjectId => id as ProjectId
export const ElementId = (id: string): ElementId => id as ElementId
export const TypeId = (id: string): TypeId => id as TypeId
export const RelationshipKey = (key: string): RelationshipKey => key as RelationshipKey

/**
 * Branded type to string converters
 * Use these when interacting with external APIs that expect plain strings
 */
export const toFloorIdString = (id: FloorId): string => id as string
export const toProjectIdString = (id: ProjectId): string => id as string
export const toElementIdString = (id: ElementId): string => id as string
export const toTypeIdString = (id: TypeId): string => id as string
export const toRelationshipKeyString = (key: RelationshipKey): string => key as string

/**
 * Nullable branded type constructors
 * These handle null/undefined values gracefully
 */
export const FloorIdOrNull = (id: string | null | undefined): FloorId | null =>
  id ? (id as FloorId) : null
export const ProjectIdOrNull = (id: string | null | undefined): ProjectId | null =>
  id ? (id as ProjectId) : null
export const ElementIdOrNull = (id: string | null | undefined): ElementId | null =>
  id ? (id as ElementId) : null
export const TypeIdOrNull = (id: string | null | undefined): TypeId | null =>
  id ? (id as TypeId) : null

/**
 * Type guards for runtime validation
 */
export const isFloorId = (value: unknown): value is FloorId =>
  typeof value === 'string' && value.length > 0

export const isProjectId = (value: unknown): value is ProjectId =>
  typeof value === 'string' && value.length > 0

export const isElementId = (value: unknown): value is ElementId =>
  typeof value === 'string' && value.length > 0

export const isTypeId = (value: unknown): value is TypeId =>
  typeof value === 'string' && value.length > 0

export const isRelationshipKey = (value: unknown): value is RelationshipKey =>
  typeof value === 'string' && value.length > 0
